import React, { useState, useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import { FeedbackType } from '../../models/assessment';
import './BulkFeedbackModal.scss';

interface SelectedFinding {
    id: string;
    spanText: string;
    feedbackTypeId?: string;
}

interface BulkFeedbackModalProps {
    /** Whether the modal is open */
    isOpen: boolean;
    /** Handler to close the modal */
    onClose: () => void;
    /** Handler to submit feedback */
    onSubmit: (feedbackType: string) => void;
    /** Number of selected findings */
    selectedCount: number;
    /** Available feedback types for the dropdown */
    feedbackTypes: FeedbackType[];
    /** Action type: 'reject' (confirm doesn't need modal) */
    actionType: 'reject';
    /** Whether submission is in progress */
    isSubmitting?: boolean;
}

/**
 * Modal for bulk reject actions with reason code selection.
 * Supports applying a single reason code to all findings or per-item overrides.
 */
function BulkFeedbackModal(props: BulkFeedbackModalProps) {
    const {
        isOpen,
        onClose,
        onSubmit,
        selectedCount,
        feedbackTypes,
        actionType,
        isSubmitting = false
    } = props;

   const [reasonCode, setReasonCode] = useState<string>('');

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            setReasonCode('');
        }
    }, [isOpen]);

    const handleReasonChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setReasonCode(e.target.value);
    }

    const handleSubmit = () => {
       if(!reasonCode){
        return;
       }
       onSubmit(reasonCode);
    };

    const handleCancel = () => {
        if (!isSubmitting) {
            onClose();
        }
    };

    const canSubmit = reasonCode !== '' && !isSubmitting;

    return (
        <Modal
            show={isOpen}
            onHide={handleCancel}
            className="bulk-feedback-modal"
            size="lg"
            backdrop={isSubmitting ? 'static' : true}
            keyboard={!isSubmitting}
        >
            <Modal.Header closeButton={!isSubmitting}>
                Reject {selectedCount} Finding{selectedCount !== 1 ? 's' : ''}
            </Modal.Header>
            
            <Modal.Body>
                <div className="bulk-feedback-content">
                    <div className="global-reason-section">
                        <label htmlFor="global-reason" className="form-label">
                            Reason Code (applies to all selected findings)
                        </label>
                        <select
                            id="global-reason"
                            className="form-control"
                            value={reasonCode}
                            onChange={handleReasonChange}
                            disabled={isSubmitting}
                        >
                            <option value="">Select a reason code...</option>
                            {feedbackTypes.map(feedbackType => (
                                <option key={feedbackType.id} value={feedbackType.id}>
                                    {feedbackType.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className='bulk-info-section'>
                        <p className='bulk-info-text'>
                            This will apply the selected reason code to {selectedCount} selected finding{selectedCount !== 1 ? 's.' : '.'}
                        </p>
                    </div>
                </div>
            </Modal.Body>
            
            <Modal.Footer>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancel}
                    disabled={isSubmitting}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="btn btn-danger"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                            Rejecting...
                        </>
                    ) : (
                        `Reject ${selectedCount} Finding${selectedCount !== 1 ? 's' : ''}`
                    )}
                </button>
            </Modal.Footer>
        </Modal>
    );
}

export default BulkFeedbackModal;